import { AutocompleteProps } from '@zeiss/beyond-online-react';
import { Autocomplete } from '@zeiss/lib-components';
import { useDebounce } from '@zeiss/lib-helpers';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { AutoCompleteLoading } from 'src/components/shared/AutoCompleteLoading';
import { DEBOUNCE_DELAY } from 'src/constants';
import { useSearchApplications } from 'src/hooks/api';
import { SearchApplicationsResponse } from 'src/types/rest/managementPortalApi';
import { ApplicationStatus as TenantApplicationStatus } from 'src/types/rest/tenant';
import { Application } from 'src/types/tenantManagement';

const searchStatusToApplicationStatus: Record<
  SearchApplicationsResponse['status'],
  Application['status']
> = {
  active: TenantApplicationStatus.active,
  deleted: TenantApplicationStatus.deleted,
  inactive: TenantApplicationStatus.inactive,
};

const toApplication = ({
  id,
  name,
  status,
  zeissDepartment,
}: SearchApplicationsResponse): Application => ({
  id,
  name,
  status: searchStatusToApplicationStatus[status],
  zeissDepartment,
});

interface ApplicationSearchProps {
  /**
   * Label for the autocomplete input
   */
  label?: string;
  /**
   * Called when the user selects an application from the autocomplete options.
   *
   * Update `selectedApplications` in the caller so the chosen application is
   * filtered out of subsequent searches.
   */
  onSelectApplication: (application: Application) => void;
  /**
   * Applications that have already been selected.
   *
   * This component uses their ids to filter them out of the autocomplete
   * options.
   */
  selectedApplications: Pick<Application, 'id'>[];
}

export const ApplicationSearch = ({
  label,
  onSelectApplication,
  selectedApplications,
}: ApplicationSearchProps) => {
  const { t } = useTranslation(['common']);

  const [helperText, setHelperText] = React.useState('');

  const [searchTerm, setSearchTerm] = React.useState('');

  const debouncedSearchTerm = useDebounce(searchTerm, DEBOUNCE_DELAY);

  const { data, isPending } = useSearchApplications(debouncedSearchTerm);

  const options = React.useMemo(() => {
    let result: AutocompleteProps['items'] = [];

    if (data && data.items) {
      const selectedApplicationIds = selectedApplications.reduce(
        (acc: Record<string, boolean>, curr) => {
          acc[curr.id] = true;
          return acc;
        },
        {},
      );

      result = data.items
        .filter(({ id }) => !selectedApplicationIds[id])
        .map(({ id, name }) => {
          return {
            label: name,
            value: id,
          };
        });
    }

    return result;
  }, [data, selectedApplications]);

  /**
   * Beyond Online reports typing through `onChange` and committed selection
   * through `onSelectionChange`. Handle the actual selection here so exact-ID
   * input still works when the selected option key equals the current input.
   */
  const handleSelectionChange = React.useCallback(
    (selectedKey: React.Key | null) => {
      if (typeof selectedKey !== 'string' || !data?.items) {
        return;
      }

      const selectedItem = data.items.find(({ id }) => id === selectedKey);
      if (selectedItem) {
        setSearchTerm('');
        onSelectApplication(toApplication(selectedItem));
      }
    },
    [data, onSelectApplication],
  );

  React.useEffect(() => {
    if (!isPending) {
      if (options.length > 0) {
        setHelperText(t('common:input.searchHelperText'));
      }
      if (options.length === 0) {
        setHelperText(
          t('common:emptyState.searchNoX', {
            x: t('common:glossary.application'),
          }),
        );
      }
    }
    if (searchTerm.length === 0) {
      setHelperText('');
    }
  }, [isPending, options, searchTerm]);

  /**
   * NOTE: Beyond Online React Workaround
   * The current (v4.31.0) implementation of the `AutoComplete` component only
   * shows the loading state if the `loading` prop is set to true **and** there
   * is no text in the input field.
   * This workaround uses the `emptyState` prop to show a loading spinner
   * when the user is typing in the input field and the request is pending.
   * This way we can show a loading spinner while the user is typing.
   */
  const loading = !!searchTerm && isPending;

  return (
    <Autocomplete
      autoComplete="off"
      emptyState={loading ? <AutoCompleteLoading /> : undefined}
      helperText={helperText}
      inputValue={searchTerm}
      items={options}
      label={label ?? t('common:glossary.application_other')}
      onChange={setSearchTerm}
      onSelectionChange={handleSelectionChange}
      placeholder={t('common:input.searchPlaceholder', {
        x: `${t('common:name')}, ${t('common:id')}`,
      }).replace('id', 'ID')}
      zIndex={1}
      zIndexList={1}
    />
  );
};
